import React from 'react';
import { useTranslation } from 'react-i18next';

import { isFalsy } from 'shared/utils/common';
import ResultOddChip from 'shared/components/outcomes/ResultOddChip';
import { vsMarketTypeKey, vsOutcomeTypeKey } from 'lib/virtualSportsI18n';
import { getSportColor, getSportOnColor } from 'shared/utils/sport-colors';
import { withBasePath } from 'shared/utils/runtimeBasePath';
import { getTeamCode } from 'shared/utils/teamDisplay';
import { getFootball3dTeamLogoUrl } from 'shared/utils/cdn';

import { formatEventTimeToHHMM } from 'features/results/formatEventTime';
import ResultCard from './ResultCard';

/**
 * FootballResultItems - Display football match result with score
 */
export default function FootballResultItems(props: any) {
  const { t } = useTranslation();
  const { current_result = {}, odds_format = 'decimal', market_type_groups, eventId: eventIdProp, eventTimestamp: eventTimestampProp, weekNumber, weekStartTime } = props;
  const {
    event_id,
    event_timestamp,
    home_team,
    away_team,
    home_team_name,
    away_team_name,
    home_team_code,
    away_team_code,
    home_team_id,
    away_team_id,
    results_football,
    result,
    odds = {},
    outcome_types,
  } = current_result as any;

  const sportColor = getSportColor(50);
  const sportOnColor = getSportOnColor(50);

  const homeTeamDisplay = getTeamCode(home_team, home_team_code) || t('HOME');
  const awayTeamDisplay = getTeamCode(away_team, away_team_code) || t('AWAY');
  // Football3d logos are on CDN by team code, not numeric id
  const homeTeamCode = String(home_team_code ?? home_team?.code ?? '').trim();
  const awayTeamCode = String(away_team_code ?? away_team?.code ?? '').trim();
  const defaultLogoUrl = withBasePath('/images/football-teams/default.webp');
  const homeLogoUrl = homeTeamCode ? getFootball3dTeamLogoUrl(homeTeamCode) : defaultLogoUrl;
  const awayLogoUrl = awayTeamCode ? getFootball3dTeamLogoUrl(awayTeamCode) : defaultLogoUrl;

  // Football3D provides top-level home_score/away_score (and results_football3d). Legacy football uses results_football/result.
  const homeGoals =
    current_result?.home_score ??
    current_result?.results_football3d?.home_score ??
    results_football?.home_score ??
    result?.home_score ??
    0;
  const awayGoals =
    current_result?.away_score ??
    current_result?.results_football3d?.away_score ??
    results_football?.away_score ??
    result?.away_score ??
    0;

  let outcomeText = '';
  if (homeGoals > awayGoals) outcomeText = t('HOME_WIN');
  else if (awayGoals > homeGoals) outcomeText = t('AWAY_WIN');
  else outcomeText = t('DRAW');

  const baseOrder = [5001, 5002, 5003, 5004, 5005, 5006];
  const oddsMarketIds = Object.keys(odds || {})
    .map((k) => Number.parseInt(String(k), 10))
    .filter((n) => Number.isFinite(n));
  const marketIds = Array.from(new Set([...baseOrder, ...oddsMarketIds]))
    .filter((mt) => (odds as any)?.[String(mt)] != null || (odds as any)?.[mt] != null)
    .filter((mt) => mt > 0);

  const getMarketName = (marketTypeId: number) => {
    const group = Object.values(market_type_groups || {}).find(
      ({ market_type_id: id }: any) => id == marketTypeId
    ) as any;
    return group?.name || t(vsMarketTypeKey(marketTypeId, false));
  };

  const getOutcomeLabel = (outcomeTypeId: any) => {
    const rawCode = (outcome_types as any)?.[String(outcomeTypeId)]?.code;
    const code = rawCode != null ? String(rawCode).replace(/_/g, '') : '';
    if (code && code !== String(outcomeTypeId)) return code;
    return t(vsOutcomeTypeKey(String(outcomeTypeId), false));
  };

  const resultsList = marketIds
    .map((marketTypeId) => {
      const marketResult = (current_result as any)?.[String(marketTypeId)] ?? (current_result as any)?.[marketTypeId];
      if (isFalsy(marketResult)) return null;

      const outcomeTypeIds = Array.isArray(marketResult) ? marketResult : [marketResult];
      const items = outcomeTypeIds
        .map((otid: any) => {
          const odd = (odds as any)?.[String(marketTypeId)]?.[String(otid)] ?? (odds as any)?.[marketTypeId]?.[otid];
          if (isFalsy(odd)) return null;
          return {
            outcomeTypeId: otid,
            outcomeLabel: getOutcomeLabel(otid),
            odd,
          };
        })
        .filter(Boolean) as Array<{ outcomeTypeId: any; outcomeLabel: string; odd: any }>;

      if (items.length === 0) return null;

      return {
        marketTypeId,
        marketName: getMarketName(marketTypeId),
        items,
      };
    })
    .filter(Boolean) as Array<{ marketTypeId: number; marketName: string; items: Array<{ outcomeTypeId: any; outcomeLabel: string; odd: any }> }>;

  const eventId = eventIdProp ?? event_id;
  const eventTimestamp = eventTimestampProp ?? event_timestamp;
  const resultsSectionHeaderText =
    weekNumber != null && weekStartTime != null
      ? `${t('RESULTS_AND_ODDS') || 'Results & Odds'} for week ${weekNumber} ${formatEventTimeToHHMM(weekStartTime) || ''}`.trim()
      : (() => {
          const timeStr = formatEventTimeToHHMM(eventTimestamp);
          return timeStr
            ? `${t('RESULTS_AND_ODDS') || 'Results & Odds'} for event #${String(eventId ?? '')} ${timeStr}`
            : `${t('RESULTS_AND_ODDS') || 'Results & Odds'} for event #${String(eventId ?? '')}`;
        })();

  return (
    <div className="football-result-items p-4">
      <div className="text-center py-2 rounded-t-lg c7-sport-gradient" style={{ backgroundColor: sportColor, color: sportOnColor }}>
        <span className="font-bold">{t('FINAL_SCORE')}</span>
      </div>

      <div className="bg-card text-card-foreground border border-border rounded-b-lg p-4">
        <div className="flex items-center justify-center gap-4">
          <div className="flex flex-col items-center flex-1">
            <div className="w-16 h-16 rounded-full bg-muted flex items-center justify-center overflow-hidden mb-2">
              <img
                src={homeLogoUrl}
                alt={homeTeamDisplay}
                className="w-12 h-12 object-contain"
                onError={(e: any) => {
                  if (e.target.src !== defaultLogoUrl) e.target.src = defaultLogoUrl;
                }}
              />
            </div>
            <span className="text-sm font-semibold truncate max-w-[100px]">
              {homeTeamDisplay}
            </span>
          </div>

          <div className="flex items-center gap-3">
            <span className="text-2xl font-bold text-foreground">{homeGoals}</span>
            <span className="text-lg text-muted-foreground">-</span>
            <span className="text-2xl font-bold text-foreground">{awayGoals}</span>
          </div>

          <div className="flex flex-col items-center flex-1">
            <div className="w-16 h-16 rounded-full bg-muted flex items-center justify-center overflow-hidden mb-2">
              <img
                src={awayLogoUrl}
                alt={awayTeamDisplay}
                className="w-12 h-12 object-contain"
                onError={(e: any) => {
                  if (e.target.src !== defaultLogoUrl) e.target.src = defaultLogoUrl;
                }}
              />
            </div>
            <span className="text-sm font-semibold truncate max-w-[100px]">
              {awayTeamDisplay}
            </span>
          </div>
        </div>

        <div className="text-center mt-4 pt-4 border-t border-border">
          <span
            className="px-4 py-1 rounded-full text-sm font-semibold"
            style={{ backgroundColor: sportColor, color: sportOnColor }}
          >
            {outcomeText}
          </span>
        </div>
      </div>

      {resultsList.length > 0 && (
        <div className="mt-4">
          <div className="text-center py-2 rounded-t-lg" style={{ backgroundColor: sportColor, color: sportOnColor }}>
            <span className="font-bold">{resultsSectionHeaderText}</span>
          </div>
          <div className="bg-card text-card-foreground border border-border rounded-b-lg p-3">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
              {resultsList.map((m) => (
                <ResultCard
                  key={String(m.marketTypeId)}
                  data-event-results-market={String(m.marketTypeId)}
                >
                  <div className="flex flex-col gap-1.5">
                    <div className="text-sm font-medium text-foreground truncate">
                      {m.marketName}
                    </div>
                    <div className="flex flex-wrap gap-x-3 gap-y-1 items-center">
                      {m.items.map((it) => (
                        <span key={String(it.outcomeTypeId)} className="inline-flex items-center gap-2">
                          <span className="text-xs font-semibold text-foreground">{it.outcomeLabel}</span>
                          <ResultOddChip odd={it.odd} oddsFormat={odds_format} />
                        </span>
                      ))}
                    </div>
                  </div>
                </ResultCard>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
